import React, { useState, useEffect } from "react";
import { Card, Tabs, Spin, Alert } from "antd";
import { useParams } from "react-router-dom";
import { getPacienteById, Paciente } from "../../api/paciente.api";
import UserInfoCard from "./InfoCard/UserInfoCard";
import AppointmentList from "./PacienteTabs/AppointmentList";
import MedicalReferences from "./MedicalReferences/index";
import EstadoDeCuenta from "./EstadoDeCuenta/EstadoDeCuenta";
import Tratamientos from "./Tratamientos/Tratamientos";
import Payments from "./Payments/Payments";
import { useAppSelector, useAppDispatch } from "../../hooks/reduxHooks";
import "../../styles/components.scss";
import { fetchPacienteAuthData } from "../../auth/authActions";
import type { PacienteAuthMeResponse } from "../../api/pacientesAuthMe.api";

const PacienteProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const authState = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  // Estados
  const [pacienteId, setPacienteId] = useState<number | null>(null);
  const [paramError, setParamError] = useState<string | null>(null);
  const [paciente, setPaciente] = useState<Paciente | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("Odontograma");
  const [isAuthenticatedUser, setIsAuthenticatedUser] =
    useState<boolean>(false);

  // Efecto 1: Validar ID de URL o usar datos del paciente autenticado
  useEffect(() => {
    setLoading(true);
    setPaciente(null);
    setError(null);
    setParamError(null);
    setIsAuthenticatedUser(false);

    // Si no hay ID en la URL, usar el paciente autenticado
    if (!id) {
      // Si tenemos datos del paciente, usarlos directamente
      if (authState.pacienteData && authState.pacienteId) {
        setPaciente(authState.pacienteData);
        setPacienteId(authState.pacienteId);
        setIsAuthenticatedUser(true);
        setLoading(false);
      } else if (authState.isLoading) {
        // Si aún está cargando los datos del paciente, esperar
        setLoading(true);
        return;
      } else if (authState.isAuthenticated) {
        // Intentar cargar los datos del paciente antes de mostrar error
        dispatch(fetchPacienteAuthData())
          .then((pacienteAuthResponse: PacienteAuthMeResponse | null) => {
            if (pacienteAuthResponse) {
              setPaciente(pacienteAuthResponse.paciente);
              setPacienteId(pacienteAuthResponse.paciente.pacienteId);
              setIsAuthenticatedUser(true);
              setLoading(false);
            } else {
              setError(
                "No se pudo obtener la información del paciente autenticado."
              );
              setLoading(false);
            }
          })
          .catch(() => {
            setError(
              "No se pudo obtener la información del paciente autenticado."
            );
            setLoading(false);
          });
      } else {
        setError("Usuario no autenticado.");
        setLoading(false);
      }
    } else {
      // Procesar ID de la URL (flujo original)
      const numId = parseInt(id, 10);
      if (!isNaN(numId) && numId > 0) {
        setPacienteId(numId);
        setParamError(null);
      } else {
        setParamError(`El ID de paciente "${id}" no es un número válido.`);
        setPacienteId(null);
        setLoading(false);
      }
    }
  }, [
    id,
    authState.pacienteData,
    authState.pacienteId,
    authState.isLoading,
    authState.error,
    authState.isAuthenticated,
    dispatch,
  ]);

  // Efecto 2: Cargar datos si ID es válido y no es el usuario autenticado
  useEffect(() => {
    const fetchPaciente = async () => {
      if (pacienteId !== null && !paramError && !isAuthenticatedUser) {
        try {
          const data = await getPacienteById(pacienteId);
          setPaciente(data);
        } catch (err: unknown) {
          if (err instanceof Error && err.message.includes("404")) {
            setError(`No se encontró un paciente con el ID ${pacienteId}.`);
          } else if (err instanceof Error) {
            setError(`Error al cargar paciente: ${err.message}`);
          } else {
            setError(
              "Ocurrió un error inesperado al cargar la información del paciente."
            );
          }
          setPaciente(null);
        } finally {
          setLoading(false);
        }
      }
    };

    if (pacienteId && !isAuthenticatedUser) {
      fetchPaciente();
    }
  }, [pacienteId, paramError, isAuthenticatedUser]);

  // --- Manejador de cambio de Tab ---
  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  // --- Renderizado Condicional ---
  if (paramError) {
    return (
      <Alert
        message="Error de Navegación"
        description={paramError}
        type="error"
        showIcon
        style={{ margin: "16px" }}
      />
    );
  }
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          padding: "50px",
        }}
      >
        <Spin
          size="large"
          tip={`Cargando paciente ${
            pacienteId ? `(ID: ${pacienteId})` : ""
          }...`}
        />
      </div>
    );
  }
  if (error) {
    return (
      <Alert
        message="Error al Cargar Datos"
        description={error}
        type="error"
        showIcon
        style={{ margin: "16px" }}
      />
    );
  }
  if (!paciente) {
    return (
      <Alert
        message="Información"
        description="No se encontró información para el paciente solicitado."
        type="warning"
        showIcon
        style={{ margin: "16px" }}
      />
    );
  }

  // --- Definición de Tabs ---
  const tabItems = [
    {
      key: "Odontograma",
      label: "Referencias Médicas",
      children: <MedicalReferences pacienteId={paciente.pacienteId} />,
    },
    {
      key: "appointment",
      label: "Citas",
      children: <AppointmentList paciente={paciente} />,
    },
    {
      key: "estadoCuenta",
      label: "Estado de Cuenta",
      children: <EstadoDeCuenta pacienteId={paciente.pacienteId} />,
    },
    {
      key: "tratamientos",
      label: "Tratamientos",
      children: <Tratamientos pacienteId={paciente.pacienteId} />,
    },
    {
      key: "pagos",
      label: "Pagos",
      children: <Payments pacienteId={paciente.pacienteId} />,
    },
  ];

  // --- Renderizado Final ---
  return (
    <div className="patient-profile-container">
      <Card
        bordered={false}
        style={{
          margin: "8px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div className="profile-header">
          <UserInfoCard paciente={paciente} />
        </div>

        <div className="profile-tabs">
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={tabItems}
            size="large"
            type="card"
          />
        </div>
      </Card>
    </div>
  );
};

export default PacienteProfile;
